import type { ReactNode } from 'react';

import { SIGN_IN_URL } from '../../api.ts';

import { ConnectReposEmpty } from './EmptyState.tsx';

interface SkeletonProps {
  title: string;
  subtitle?: string;
  /** Number of placeholder rows to render. */
  rows?: number;
}

/** Loading placeholder shown while a snapshot is in flight. */
export function Skeleton({ title, subtitle, rows = 6 }: SkeletonProps) {
  return (
    <section aria-busy='true' aria-live='polite'>
      <h1 className='snap-h1'>{title}</h1>
      {subtitle ? <p className='snap-subtitle'>{subtitle}</p> : null}
      <div className='snap-skeleton'>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className='snap-skeleton__row' />
        ))}
      </div>
    </section>
  );
}

interface ErrorViewProps {
  title: string;
  error: Error;
  children?: ReactNode;
}

/** Error fallback shown when a snapshot query fails. */
export function ErrorView({ title, error, children }: ErrorViewProps) {
  return (
    <section>
      <h1 className='snap-h1'>{title}</h1>
      <div className='snap-error' role='alert'>
        <p>
          <strong>Couldn’t load the snapshot.</strong>
        </p>
        <p className='snap-error__message'>{error.message}</p>
        {children}
      </div>
    </section>
  );
}

interface EmptyViewProps {
  title: string;
  subtitle?: string;
}

/** Shown when the view loaded successfully but the org has no data yet. */
export function EmptyView({ title, subtitle }: EmptyViewProps) {
  return (
    <section>
      <h1 className='snap-h1'>{title}</h1>
      {subtitle ? <p className='snap-subtitle'>{subtitle}</p> : null}
      <ConnectReposEmpty title='No data yet' />
    </section>
  );
}

interface SignInViewProps {
  title: string;
}

/** Shown to signed-out visitors on views that require an org. */
export function SignInView({ title }: SignInViewProps) {
  return (
    <section>
      <h1 className='snap-h1'>{title}</h1>
      <p className='snap-subtitle'>Sign in to load this view.</p>
      <p>
        <a href={SIGN_IN_URL} className='btn'>
          Continue with WorkOS
        </a>
      </p>
    </section>
  );
}
